/**
 * Shared Input Component
 * Following Feature-Based Modular Architecture and SOLID principles
 * Reusable input component with consistent styling and validation
 */

import React, { forwardRef } from 'react'

import { InputProps } from '@/shared/types/common.types'

const Input = React.memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        type = 'text',
        placeholder,
        value,
        defaultValue,
        disabled = false,
        required = false,
        onChange,
        onBlur,
        name,
        id,
        className = '',
        testId,
        children,
        ...props
      },
      ref,
    ) => {
      // Base input classes
      const baseClasses = [
        'block',
        'w-full',
        'px-3',
        'py-2',
        'border',
        'rounded-md',
        'text-sm',
        'transition-colors',
        'duration-200',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-crypto-primary/20',
        'focus:border-crypto-primary',
        'disabled:opacity-50',
        'disabled:cursor-not-allowed',
        'disabled:bg-gray-50',
        'dark:disabled:bg-crypto-card/50',
      ]

      // Theme-aware classes
      const themeClasses = [
        'bg-white',
        'dark:bg-crypto-card',
        'border-gray-300',
        'dark:border-crypto-border',
        'text-gray-900',
        'dark:text-crypto-text',
        'placeholder-gray-500',
        'dark:placeholder-gray-400',
      ]

      // Combine all classes
      const inputClasses = [...baseClasses, ...themeClasses, className].filter(Boolean).join(' ')

      return (
        <input
          ref={ref}
          type={type}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          required={required}
          onChange={onChange}
          onBlur={onBlur}
          name={name}
          id={id}
          className={inputClasses}
          data-testid={testId}
          {...props}
        />
      )
    },
  ),
)

Input.displayName = 'Input'

export default Input
